import { useMemo, useState } from "react";
import { clsx } from "clsx";
import {
  CalendarCheck,
  CircleDot,
  FileCheck,
  Flag,
  FolderPlus,
  Gavel,
  type LucideIcon,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Pencil,
  Route,
  Stamp,
  Upload,
} from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { Button } from "@/components/ui/Button";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { useParcels, useProceedings, useProjectEvents } from "../hooks";
import type { PowerProject, ProjectEvent, ProjectEventKind } from "../types";
import { fmtDayHeading, fmtTime } from "../utils";
import { EmptyState, SectionCard } from "./ui";

type Tone = "indigo" | "blue" | "emerald" | "amber" | "purple" | "neutral";

// Marker styling per tone — light tints + colored icon, matching the Badge color kit.
const TONE: Record<Tone, { icon: string; ring: string }> = {
  indigo: { icon: "text-indigo-600", ring: "border-indigo-200 bg-indigo-50" },
  blue: { icon: "text-blue-600", ring: "border-blue-200 bg-blue-50" },
  emerald: { icon: "text-emerald-600", ring: "border-emerald-200 bg-emerald-50" },
  amber: { icon: "text-amber-600", ring: "border-amber-300 bg-amber-50" },
  purple: { icon: "text-purple-600", ring: "border-purple-200 bg-purple-50" },
  neutral: { icon: "text-[var(--color-text-secondary)]", ring: "border-[var(--color-border)] bg-[var(--color-bg-elevated)]" },
};

const KIND_META: Record<ProjectEventKind, { tone: Tone; Icon: LucideIcon }> = {
  project_created: { tone: "indigo", Icon: FolderPlus },
  route_version_created: { tone: "blue", Icon: Upload },
  parcel_added: { tone: "emerald", Icon: MapPin },
  parcel_entered_route: { tone: "emerald", Icon: LogIn },
  parcel_left_route: { tone: "amber", Icon: LogOut },
  parcel_route_changed: { tone: "amber", Icon: Route },
  parcel_status_changed: { tone: "blue", Icon: CircleDot },
  parcel_data_edited: { tone: "neutral", Icon: Pencil },
  owner_access_status_changed: { tone: "emerald", Icon: FileCheck },
  owner_access_route_extended: { tone: "amber", Icon: Route },
  proceeding_status_changed: { tone: "emerald", Icon: Stamp },
  proceeding_item: { tone: "neutral", Icon: Mail },
  decision_registered: { tone: "purple", Icon: Gavel },
  milestone_changed: { tone: "purple", Icon: Flag },
  topic_status_changed: { tone: "indigo", Icon: CircleDot },
  kpi_review: { tone: "indigo", Icon: CalendarCheck },
  wpw_mail_letter: { tone: "blue", Icon: Mail },
};

const FALLBACK = { tone: "neutral" as Tone, Icon: CircleDot };

// Kind groups for the filter dropdown (keeps the list short and meaningful).
const KIND_FILTERS: { value: string; label: string }[] = [
  { value: "route_version_created", label: "Wgranie trasy (DXF)" },
  { value: "parcel_route_changed", label: "Zmiana trasy na działce" },
  { value: "parcel_added", label: "Nowe działki" },
  { value: "parcel_status_changed", label: "Zmiany statusu działki" },
  { value: "parcel_data_edited", label: "Edycja danych działki" },
  { value: "owner_access_status_changed", label: "Tytuły dostępu (właściciele)" },
  { value: "owner_access_route_extended", label: "Rozciągnięcie na aktualną wersję" },
  { value: "proceeding_status_changed", label: "Postępowania (otwarcie/zamknięcie)" },
  { value: "proceeding_item", label: "Pozycje postępowań (wnioski/odpowiedzi)" },
  { value: "decision_registered", label: "Zarejestrowane decyzje" },
  { value: "milestone_changed", label: "Kamienie milowe" },
  { value: "topic_status_changed", label: "Status tematu (DYR-TECH)" },
  { value: "wpw_mail_letter", label: "WPW — ścieżka listowna" },
];

function TagChip({ label, onClick }: { label: string; onClick?: () => void }) {
  const cls =
    "inline-flex items-center rounded-sm text-[10px] font-mono leading-none px-1.5 py-1 border border-[var(--color-border)] bg-[var(--color-bg-base)] text-[var(--color-text-secondary)] whitespace-nowrap transition-colors";
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={`${cls} cursor-pointer hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]`}
    >
      {label}
    </button>
  ) : (
    <span className={cls}>{label}</span>
  );
}

/** Grouped-by-day vertical timeline. Reused by the tab and the parcel drawer. */
export function ProjectEventList({
  events,
  onSelectParcel,
  onSelectOffice,
}: {
  events: ProjectEvent[];
  onSelectParcel?: (id: string) => void;
  onSelectOffice?: (id: string) => void;
}) {
  const groups = useMemo(() => {
    const m = new Map<string, ProjectEvent[]>();
    for (const e of events) {
      const day = e.occurred_at.slice(0, 10);
      const arr = m.get(day) ?? [];
      arr.push(e);
      m.set(day, arr);
    }
    return [...m.entries()]; // API returns newest-first → days and rows already descending
  }, [events]);

  if (events.length === 0) return <EmptyState>Brak zdarzeń.</EmptyState>;

  return (
    <div className="space-y-6">
      {groups.map(([day, evs]) => (
        <div key={day}>
          <div className="flex items-center gap-2 mb-2.5">
            <span className="h-[3px] w-3 bg-[var(--color-primary)]" />
            <h3 className="font-mono text-[0.7rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">
              {fmtDayHeading(day)}
            </h3>
            <span className="font-mono text-[10px] text-[var(--color-text-muted)]">({evs.length})</span>
          </div>
          <ol className="relative ml-3 border-l border-[var(--color-border)]">
            {evs.map((e) => {
              const meta = KIND_META[e.kind] ?? FALLBACK;
              const tone = TONE[meta.tone];
              return (
                <li
                  key={e.id}
                  className="relative pl-6 pr-2 py-1.5 transition-colors hover:bg-[var(--color-bg-elevated)]"
                >
                  <span
                    className={clsx(
                      "absolute -left-[11px] top-1 flex h-[22px] w-[22px] items-center justify-center rounded-full border",
                      tone.ring,
                    )}
                  >
                    <meta.Icon size={12} className={tone.icon} />
                  </span>
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-medium leading-snug text-[var(--color-text-primary)]">{e.title}</p>
                    <div className="flex shrink-0 flex-col items-end pt-0.5 leading-tight">
                      <time className="font-mono text-[11px] tabular-nums text-[var(--color-text-secondary)]">
                        {fmtTime(e.occurred_at)}
                      </time>
                      {e.actor_name && (
                        <span className="font-mono text-[10px] text-[var(--color-text-muted)]">{e.actor_name}</span>
                      )}
                    </div>
                  </div>
                  {e.detail && (
                    <p className="mt-1 text-[12px] leading-relaxed text-[var(--color-text-secondary)] whitespace-pre-wrap">
                      {e.detail}
                    </p>
                  )}
                  {(e.parcel_teryt || e.office_name || e.route_version_label || (e.owner_name && !e.office_name)) && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {e.parcel_teryt && (
                        <TagChip
                          label={`działka ${e.parcel_teryt}`}
                          onClick={onSelectParcel && e.parcel ? () => onSelectParcel(e.parcel!) : undefined}
                        />
                      )}
                      {e.office_name && (
                        <TagChip
                          label={e.office_name}
                          onClick={onSelectOffice && e.office ? () => onSelectOffice(e.office!) : undefined}
                        />
                      )}
                      {e.route_version_label && <TagChip label={`wersja ${e.route_version_label}`} />}
                      {e.owner_name && !e.office_name && <TagChip label={e.owner_name} />}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </div>
  );
}

export function ProgressTimelineTab({ connection }: { connection: PowerProject }) {
  const [kinds, setKinds] = useState<string[]>([]);
  const [parcelIds, setParcelIds] = useState<string[]>([]);
  const [officeIds, setOfficeIds] = useState<string[]>([]);
  const [ownerIds, setOwnerIds] = useState<string[]>([]);
  const [proceedingIds, setProceedingIds] = useState<string[]>([]);

  const filters = useMemo(() => {
    const f: Record<string, string> = {};
    if (kinds.length) f.kind = kinds.join(",");
    if (parcelIds.length) f.parcel = parcelIds.join(",");
    if (officeIds.length) f.office = officeIds.join(",");
    if (ownerIds.length) f.owner = ownerIds.join(",");
    if (proceedingIds.length) f.proceeding = proceedingIds.join(",");
    return f;
  }, [kinds, parcelIds, officeIds, ownerIds, proceedingIds]);

  const { data: events, isLoading } = useProjectEvents(connection.id, filters);
  const { data: parcels } = useParcels(connection.id);
  const { data: proceedings } = useProceedings({ connection: connection.id });

  const parcelOptions = useMemo(
    () => (parcels ?? []).map((p) => ({ value: p.id, label: p.teryt })),
    [parcels],
  );
  const ownerOptions = useMemo(
    () =>
      (parcels ?? []).flatMap((p) =>
        (p.owners ?? []).map((o) => ({ value: o.id, label: `${o.full_name} · ${p.teryt}` })),
      ),
    [parcels],
  );
  const proceedingOptions = useMemo(
    () =>
      (proceedings ?? []).map((p) => ({
        value: p.id,
        label: `${p.type_name}${p.office_name ? ` · ${p.office_name}` : ""}`,
      })),
    [proceedings],
  );
  // Only offices this project actually touches — owners that are offices (działki)
  // and proceeding recipients (postępowania) — not the system-wide registry.
  const officeOptions = useMemo(() => {
    const used = new Map<string, string>();
    for (const p of parcels ?? [])
      for (const o of p.owners ?? [])
        if (o.office && o.office_name) used.set(o.office, o.office_name);
    for (const pr of proceedings ?? [])
      if (pr.recipient_office && pr.office_name) used.set(pr.recipient_office, pr.office_name);
    return [...used.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, "pl"));
  }, [parcels, proceedings]);
  const hasFilters = !!(kinds.length || parcelIds.length || officeIds.length || ownerIds.length || proceedingIds.length);

  // Pełna szerokość zakładki: filtry w jednym rzędzie, a lista zdarzeń przewija się
  // we własnym oknie (max-h), żeby długa historia nie ciągnęła całej strony.
  return (
    <div className="space-y-4">
      <SectionCard
        title="Historia postępów — filtry"
        action={
          hasFilters ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setKinds([]); setParcelIds([]); setOfficeIds([]); setOwnerIds([]); setProceedingIds([]); }}
            >
              Wyczyść
            </Button>
          ) : undefined
        }
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
          <div className="block">
            <span className="section-label">Typ zdarzenia (pozycji)</span>
            <div className="mt-1">
              <MultiSelect options={KIND_FILTERS} value={kinds} placeholder="— wszystkie typy —" onChange={setKinds} />
            </div>
          </div>
          <div className="block">
            <span className="section-label">Działka</span>
            <div className="mt-1">
              <MultiSelect options={parcelOptions} value={parcelIds} placeholder="— wszystkie działki —" onChange={setParcelIds} />
            </div>
          </div>
          <div className="block">
            <span className="section-label">Właściciel</span>
            <div className="mt-1">
              <MultiSelect options={ownerOptions} value={ownerIds} placeholder="— wszyscy właściciele —" onChange={setOwnerIds} />
            </div>
          </div>
          <div className="block">
            <span className="section-label">Postępowanie</span>
            <div className="mt-1">
              <MultiSelect options={proceedingOptions} value={proceedingIds} placeholder="— wszystkie postępowania —" onChange={setProceedingIds} />
            </div>
          </div>
          <div className="block">
            <span className="section-label">Urząd</span>
            <div className="mt-1">
              <MultiSelect options={officeOptions} value={officeIds} placeholder="— wszystkie urzędy —" onChange={setOfficeIds} />
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title={`Zdarzenia${events ? ` (${events.length})` : ""}`} bodyClassName="p-0">
        <div className="max-h-[calc(100vh-700px)] min-h-[320px] overflow-y-auto p-4">
          {isLoading ? (
            <div className="py-8 flex justify-center"><Spinner /></div>
          ) : (
            <ProjectEventList
              events={events ?? []}
              onSelectParcel={(id) => setParcelIds([id])}
              onSelectOffice={(id) => setOfficeIds([id])}
            />
          )}
        </div>
      </SectionCard>
    </div>
  );
}
